import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { CdkDrag, CdkDragDrop, CdkDragHandle, CdkDropList, transferArrayItem } from '@angular/cdk/drag-drop';
import { NgbDropdownModule, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { forkJoin, of } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { StatefulComponent } from '../../components/stateful/stateful.component';
import { ConfirmationModalComponent } from '../../components/confirmation-modal/confirmation-modal.component';
import { WorkItemModalComponent, WorkItemModalResult } from '../../components/work-item-modal/work-item-modal.component';
import { RejectWorkItemModalComponent } from '../../components/reject-work-item-modal/reject-work-item-modal.component';
import { setContainerTypeAction } from '../../ngrx/actions/global.actions';
import {
  InvoiceService,
  InvoiceBoardResponseDto,
  InvoiceBoardVehicleDto,
  InvoiceBoardWorkItemDto,
  InvoiceStatusResponseDto,
  WorkItemStatusResponseDto
} from '../../services/invoice.service';
import { AuthorizeService } from '../../services/authorize.service';
import { AlertService } from '../../services/alert.service';
import { CarfaxService } from '../../services/carfax.service';
import { Customer } from '../../models/Customer.model';
import { invoiceModuleBaseRoute, invoiceModuleRoutes } from '../../routes/invoice-routes';

// A work item flattened out of its vehicle, labeled for display on the board.
interface WorkItemVm extends InvoiceBoardWorkItemDto {
  VehicleLabel: string;
}

interface BoardSection {
  status: string;
  label: string;
  next: string | null;
  nextLabel: string | null;
}

@Component({
  selector: 'app-invoices',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterLink, CdkDropList, CdkDrag, CdkDragHandle, NgbDropdownModule],
  templateUrl: './invoices.component.html',
  styleUrl: './invoices.component.scss'
})
export class InvoicesComponent extends StatefulComponent implements OnInit {
  invoiceRoute = invoiceModuleBaseRoute;
  invoiceModuleRoutes = invoiceModuleRoutes;

  // Board sections in vertical display order.
  readonly sections: BoardSection[] = [
    { status: 'MECHANIC_REVIEW', label: 'Mechanic Review', next: 'CUSTOMER_REVIEW', nextLabel: 'Send to Customer Review' },
    { status: 'CUSTOMER_REVIEW', label: 'Customer Review', next: 'TODO', nextLabel: 'Approve Charges' },
    { status: 'TODO', label: 'To Do', next: 'IN_PROGRESS', nextLabel: 'Start Work' },
    { status: 'IN_PROGRESS', label: 'In Progress', next: 'AWAITING_PAYMENT', nextLabel: 'Move to Awaiting Payment' },
    { status: 'AWAITING_PAYMENT', label: 'Awaiting Payment', next: 'COMPLETE', nextLabel: 'Mark Complete' },
    { status: 'COMPLETE', label: 'Complete', next: null, nextLabel: null }
  ];
  readonly sectionIds = this.sections.map(s => s.status);
  readonly workItemColumns = [
    { status: 'TODO', label: 'To Do' },
    { status: 'DOING', label: 'Doing' },
    { status: 'DONE', label: 'Done' }
  ];
  // Mobile fallback for drag & drop: per-lane move buttons on work items.
  readonly mobileWorkItemMoves: { [status: string]: { target: string; label: string }[] } = {
    TODO: [{ target: 'DOING', label: 'Move to Doing' }],
    DOING: [
      { target: 'TODO', label: 'Move to To Do' },
      { target: 'DONE', label: 'Move to Done' }
    ],
    DONE: [{ target: 'DOING', label: 'Move to Doing' }]
  };

  board: InvoiceBoardResponseDto[] = [];
  grouped: { [status: string]: InvoiceBoardResponseDto[] } = {};
  // Accordion state per section; all sections start expanded.
  expandedSections: { [status: string]: boolean } = {};
  // Collapse state per invoice card; cards start collapsed.
  expandedInvoices: { [invoiceId: string]: boolean } = {};
  // Rejections staged by the customer during CUSTOMER_REVIEW, keyed by
  // WorkItemId. Persisted only when the review is submitted.
  pendingRejections: { [workItemId: string]: string } = {};
  workItemGroups: { [invoiceId: string]: { [status: string]: WorkItemVm[] } } = {};
  allWorkItems: { [invoiceId: string]: WorkItemVm[] } = {};

  invoiceStatuses: InvoiceStatusResponseDto[] = [];
  workItemStatuses: WorkItemStatusResponseDto[] = [];
  mechanics: Customer[] = [];

  // Board filter: only show invoices with a work item assigned to this
  // mechanic. Empty string shows all mechanics (the default).
  mechanicFilter: string = '';

  // CARFAX export: report day (yyyy-MM-dd from the date input), defaults to today.
  carfaxExportDate: string = new Date().toLocaleDateString('en-CA');
  exportingCarfax = false;

  loading = false;
  error: string | null = null;

  constructor(
    private invoiceService: InvoiceService,
    private authService: AuthorizeService,
    private alertService: AlertService,
    private carfaxService: CarfaxService,
    private ngbModal: NgbModal
  ) {
    super();
  }

  ngOnInit() {
    this.dispatch(setContainerTypeAction({ containerType: 'normal' }));
    for (const section of this.sections) {
      this.expandedSections[section.status] = true;
    }
    this.loadStatuses();
    if (this.hasEditAll) {
      this.loadMechanics();
    }
    this.loadBoard();
  }

  isExpanded(status: string): boolean {
    return this.expandedSections[status] !== false;
  }

  toggleSection(status: string): void {
    this.expandedSections[status] = !this.isExpanded(status);
  }

  isInvoiceExpanded(invoice: InvoiceBoardResponseDto): boolean {
    return this.expandedInvoices[invoice.InvoiceId] === true;
  }

  toggleInvoice(invoice: InvoiceBoardResponseDto): void {
    this.expandedInvoices[invoice.InvoiceId] = !this.isInvoiceExpanded(invoice);
  }

  // ============= PERMISSIONS =============

  get hasEditAll(): boolean {
    return this.permissions?.includes('EDIT_ALL_INVOICES') || false;
  }

  get hasViewAll(): boolean {
    return this.permissions?.includes('VIEW_ALL_INVOICES') || false;
  }

  get hasEdit(): boolean {
    return this.permissions?.includes('EDIT_INVOICES') || false;
  }

  /** Customers can only approve invoices they own. */
  canApprove(invoice: InvoiceBoardResponseDto): boolean {
    return this.hasEdit && invoice.CustUserId === this.user?.userId;
  }

  /**
   * Who can act on a customer review: the owning customer, or staff recording
   * the customer's decision for them (approved in person or over the phone).
   */
  canReviewCharges(invoice: InvoiceBoardResponseDto): boolean {
    return this.canApprove(invoice) || this.hasEditAll;
  }

  /** The review button reads differently for the customer than for staff. */
  customerReviewLabel(invoice: InvoiceBoardResponseDto): string {
    const rejected = this.pendingRejectionCount(invoice);
    if (rejected > 0) {
      return `Submit Review (${rejected} rejected)`;
    }
    return this.canApprove(invoice) ? 'Approve Charges' : 'Approve for Customer';
  }

  /** Whether the user has permission to drag work items at all. */
  canDragWorkItem(): boolean {
    return this.hasEditAll;
  }

  /**
   * Work items can only be moved by the mechanic assigned to them. Unassigned
   * work items are claimable — moving one assigns it to the mover.
   */
  canMoveWorkItem(workItem: InvoiceBoardWorkItemDto): boolean {
    return this.hasEditAll && (!workItem.EmpUserId || workItem.EmpUserId === this.user?.userId);
  }

  workItemMoveTooltip(workItem: InvoiceBoardWorkItemDto): string {
    if (this.canMoveWorkItem(workItem)) {
      return workItem.EmpUserId ? '' : 'Moving this work item will assign it to you';
    }
    return `Only ${this.getMechanicLabel(workItem)} can move this work item`;
  }

  /** Whether the move-to-next button shows for this invoice in this section. */
  canMoveNext(section: BoardSection, invoice: InvoiceBoardResponseDto): boolean {
    if (!section.next) return false;

    switch (section.status) {
      case 'CUSTOMER_REVIEW':
        return this.canReviewCharges(invoice);
      case 'IN_PROGRESS':
        return this.hasEditAll && !this.hasOutstandingWorkItems(invoice);
      default:
        return this.hasEditAll;
    }
  }

  /** Work items can be added/edited in these phases (with a warning after MECHANIC_REVIEW). */
  canModifyWorkItems(section: BoardSection): boolean {
    return this.hasEditAll && ['MECHANIC_REVIEW', 'TODO', 'IN_PROGRESS'].includes(section.status);
  }

  /**
   * The compact "Add Work Item" button in the card actions row. MECHANIC_REVIEW
   * and TODO cards show the prominent dashed empty-state button instead while
   * they have no work items; the IN_PROGRESS board has no such empty state, so
   * it always gets this one.
   */
  showAddWorkItemButton(section: BoardSection, invoice: InvoiceBoardResponseDto): boolean {
    if (!this.canModifyWorkItems(section)) return false;
    return section.status === 'IN_PROGRESS' || !!this.allWorkItems[invoice.InvoiceId]?.length;
  }

  /** Post-work phases: work items are viewable (read-only modal) but locked. */
  isReadOnlyWorkItemSection(section: BoardSection): boolean {
    return ['AWAITING_PAYMENT', 'COMPLETE'].includes(section.status);
  }

  /**
   * The view (eye) icon opens the work item read-only: post-work phases for
   * everyone, plus TODO for viewers without edit rights (customers reviewing
   * the charges they approved). Mechanics get the edit icon there instead.
   */
  showReadOnlyWorkItemButton(section: BoardSection): boolean {
    return this.isReadOnlyWorkItemSection(section) ||
      (section.status === 'TODO' && !this.canModifyWorkItems(section));
  }

  /** Sections whose work-item rows show the time/parts/labor/sub-total column. */
  showWorkItemValues(section: BoardSection): boolean {
    // TODO holds approved charges — both the customer (EDIT_INVOICES) and the
    // shop (EDIT_ALL_INVOICES) can still see the pricing they agreed to.
    if (section.status === 'TODO') {
      return this.hasEdit || this.hasEditAll;
    }
    return section.status === 'MECHANIC_REVIEW' || this.isReadOnlyWorkItemSection(section);
  }

  // ============= CARFAX EXPORT =============

  /** Download the CARFAX Car Care file for invoices paid on the selected day. */
  exportCarfax(): void {
    if (!this.carfaxExportDate || this.exportingCarfax) return;

    // Date input gives yyyy-MM-dd; the API expects MMDDYYYY.
    const [year, month, day] = this.carfaxExportDate.split('-');
    const reportDay = `${month}${day}${year}`;

    this.exportingCarfax = true;
    this.subs.push(this.carfaxService.downloadProdFeed(reportDay).subscribe({
      next: (response) => {
        this.exportingCarfax = false;
        const content = response.body ?? '';

        // Header-only file means nothing was paid that day — say so instead
        // of handing over an empty export.
        if (content.trim().split('\r\n').length <= 1) {
          this.alertService.showSnackBar(
            `No completed invoices were paid on ${month}/${day}/${year}.`);
          return;
        }

        const disposition = response.headers.get('Content-Disposition') ?? '';
        const fileName = /filename="([^"]+)"/.exec(disposition)?.[1]
          ?? `Carfax_PROD_RO_${reportDay}.txt`;
        this.saveTextFile(content, fileName);
      },
      error: (error) => {
        this.exportingCarfax = false;
        console.error('[Invoices] CARFAX export failed:', error);
        this.alertService.showSnackBar('Failed to generate the CARFAX file.');
      }
    }));
  }

  private saveTextFile(content: string, fileName: string): void {
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = fileName;
    anchor.click();
    URL.revokeObjectURL(url);
  }

  // ============= DATA LOADING =============

  private loadStatuses(): void {
    this.subs.push(this.invoiceService.getInvoiceStatuses().subscribe({
      next: (statuses) => this.invoiceStatuses = statuses,
      error: (error) => console.error('[Invoices] Failed to load invoice statuses:', error)
    }));
    this.subs.push(this.invoiceService.getWorkItemStatuses().subscribe({
      next: (statuses) => this.workItemStatuses = statuses,
      error: (error) => console.error('[Invoices] Failed to load work item statuses:', error)
    }));
  }

  private loadMechanics(): void {
    this.subs.push(this.authService.getMechanics().subscribe({
      next: (mechanics) => {
        this.mechanics = mechanics;
      },
      error: (error) => console.error('[Invoices] Failed to load mechanics:', error)
    }));
  }

  onMechanicFilterChange(): void {
    this.regroup();
  }

  private loadBoard(): void {
    this.loading = true;
    this.error = null;

    this.subs.push(this.invoiceService.getInvoiceBoard().subscribe({
      next: (board) => {
        this.board = board;
        this.regroup();
        this.loading = false;
      },
      error: (error) => {
        this.loading = false;
        this.error = 'Failed to load invoices.';
        console.error('[Invoices] Failed to load the invoice board:', error);
      }
    }));
  }

  /** Rebuild the section and work-item groupings from the flat board data. */
  private regroup(): void {
    this.grouped = {};
    for (const section of this.sections) {
      this.grouped[section.status] = [];
    }

    this.workItemGroups = {};
    this.allWorkItems = {};

    for (const invoice of this.board) {
      const flattened: WorkItemVm[] = invoice.Vehicles.flatMap(vehicle =>
        vehicle.WorkItems.map(workItem => ({
          ...workItem,
          VehicleLabel: this.getVehicleDisplayName(vehicle)
        }))
      );

      // The mechanic filter hides invoices with no work item assigned to them.
      const matchesFilter = !this.mechanicFilter ||
        flattened.some(workItem => workItem.EmpUserId === this.mechanicFilter);
      if (matchesFilter) {
        (this.grouped[invoice.InvoiceStatus] ??= []).push(invoice);
      }

      this.allWorkItems[invoice.InvoiceId] = flattened;
      this.workItemGroups[invoice.InvoiceId] = { TODO: [], DOING: [], DONE: [] };
      for (const workItem of flattened) {
        (this.workItemGroups[invoice.InvoiceId][workItem.WorkItemStatus] ??= []).push(workItem);
      }
    }
  }

  // ============= DISPLAY HELPERS =============

  getVehicleDisplayName(vehicle: InvoiceBoardVehicleDto): string {
    const parts = [vehicle.Year, vehicle.Make, vehicle.Model].filter(p => !!p);
    return parts.length > 0 ? parts.join(' ') : 'Unknown Vehicle';
  }

  /**
   * Header label for the invoice's car. Multi-vehicle invoices collapse to a
   * single marker; the individual cars are still listed in the card body.
   * Returns '' when there are no vehicles so the header line is omitted.
   */
  getInvoiceVehicleLabel(invoice: InvoiceBoardResponseDto): string {
    const vehicles = invoice.Vehicles ?? [];
    if (vehicles.length === 0) return '';
    if (vehicles.length > 1) return 'Multiple Vehicles';
    return this.getVehicleDisplayName(vehicles[0]);
  }

  getCustomerDisplayName(invoice: InvoiceBoardResponseDto): string {
    return `${invoice.CustFirstName} ${invoice.CustLastName}`.trim() || invoice.CustEmail;
  }

  /**
   * Work item rows carry their vehicle, so the standalone vehicle list only
   * shows when the invoice has no work items to carry it.
   */
  showVehicleSummary(section: BoardSection, invoice: InvoiceBoardResponseDto): boolean {
    return !(this.allWorkItems[invoice.InvoiceId]?.length);
  }

  getMechanicLabel(workItem: InvoiceBoardWorkItemDto): string {
    if (!workItem.EmpUserId) return 'Unassigned';
    return `${workItem.EmpFirstName || ''} ${workItem.EmpLastName || ''}`.trim() || 'Unassigned';
  }

  // Render a decimal hour value as "6 hr 30 min".
  formatShopTime(totalHours: number): string {
    const hours = Math.floor(totalHours);
    const minutes = Math.round((totalHours - hours) * 60);
    const parts: string[] = [];
    if (hours > 0) {
      parts.push(`${hours} hr`);
    }
    if (minutes > 0) {
      parts.push(`${minutes} min`);
    }
    return parts.join(' ') || '0 min';
  }

  // All pricing below is server-computed (util/work-item-pricing.ts on the
  // API); the board reloads after every persisting change, so these values
  // are always current.
  laborAmount(workItem: InvoiceBoardWorkItemDto): number {
    return workItem.LaborAmount ?? 0;
  }

  lineSubtotal(workItem: InvoiceBoardWorkItemDto): number {
    return workItem.LineTotal ?? 0;
  }

  invoiceSubtotal(invoice: InvoiceBoardResponseDto): number {
    return invoice.SubTotal;
  }

  invoiceTax(invoice: InvoiceBoardResponseDto): number {
    return invoice.Tax;
  }

  invoiceTotal(invoice: InvoiceBoardResponseDto): number {
    return invoice.Total;
  }

  hasOutstandingWorkItems(invoice: InvoiceBoardResponseDto): boolean {
    return (this.allWorkItems[invoice.InvoiceId] || []).some(wi => wi.WorkItemStatus !== 'DONE');
  }

  // Board work items are already filtered to active server-side.
  private hasUnassignedWorkItems(invoice: InvoiceBoardResponseDto): boolean {
    return (this.allWorkItems[invoice.InvoiceId] || []).some(wi => !wi.EmpUserId);
  }

  private hasNoWorkItems(invoice: InvoiceBoardResponseDto): boolean {
    return (this.allWorkItems[invoice.InvoiceId] || []).length === 0;
  }

  /**
   * Customer review requires at least one work item, each with a mechanic —
   * labor pricing depends on the assigned mechanic's hourly rate. The API
   * enforces both rules too; the snackbar just fails fast.
   */
  private blockUnassignedMove(invoice: InvoiceBoardResponseDto, target: string): boolean {
    if (target !== 'CUSTOMER_REVIEW') return false;
    if (this.hasNoWorkItems(invoice)) {
      this.alertService.showSnackBar(
        'Add at least one work item before sending this invoice to customer review.');
      return true;
    }
    if (this.hasUnassignedWorkItems(invoice)) {
      this.alertService.showSnackBar(
        'Assign a mechanic to every work item before sending this invoice to customer review.');
      return true;
    }
    return false;
  }

  workItemListId(invoice: InvoiceBoardResponseDto, status: string): string {
    return `wi::${invoice.InvoiceId}::${status}`;
  }

  workItemListIds(invoice: InvoiceBoardResponseDto): string[] {
    return this.workItemColumns.map(column => this.workItemListId(invoice, column.status));
  }

  // ============= INVOICE MOVES =============

  /** Button-driven move to the section's next status. */
  moveNext(section: BoardSection, invoice: InvoiceBoardResponseDto): void {
    if (!section.next) return;
    const target = section.next;
    if (this.blockUnassignedMove(invoice, target)) return;
    this.confirmMove(this.moveConfirmationMessage(invoice, section.label, target), 'Move')
      .then((confirmed) => {
        if (confirmed) {
          this.moveInvoice(invoice, target);
        }
      });
  }

  /** Drag-and-drop between status sections (EDIT_ALL_INVOICES only). */
  onInvoiceDropped(event: CdkDragDrop<InvoiceBoardResponseDto[]>): void {
    if (event.previousContainer === event.container) return;

    const droppedInvoice = event.item.data as InvoiceBoardResponseDto;
    if (this.blockUnassignedMove(droppedInvoice, event.container.id)) {
      // Card was never transferred; regroup for a clean re-render anyway.
      this.regroup();
      return;
    }

    // Move the card immediately for a smooth drag, then confirm and persist.
    transferArrayItem(
      event.previousContainer.data,
      event.container.data,
      event.previousIndex,
      event.currentIndex
    );

    const invoice = event.item.data as InvoiceBoardResponseDto;
    const targetStatusName = event.container.id;
    const fromLabel = this.getSectionLabel(invoice.InvoiceStatus);
    this.confirmMove(this.moveConfirmationMessage(invoice, fromLabel, targetStatusName), 'Move')
      .then((confirmed) => {
        if (confirmed) {
          this.moveInvoice(invoice, targetStatusName, true);
        } else {
          // Status was never persisted, so regrouping snaps the card back.
          this.regroup();
        }
      });
  }

  /** Shared yes/no modal for invoice status moves; resolves false on dismiss. */
  private confirmMove(message: string, confirmText: string, title = 'Move Invoice?'): Promise<boolean> {
    const confirmRef = this.ngbModal.open(ConfirmationModalComponent, { centered: true });
    confirmRef.componentInstance.modalRef = confirmRef;
    confirmRef.componentInstance.title = title;
    confirmRef.componentInstance.message = message;
    confirmRef.componentInstance.confirmButtonText = confirmText;
    confirmRef.componentInstance.cancelButtonText = 'Cancel';
    confirmRef.componentInstance.confirmButtonClass = 'btn-primary';
    return confirmRef.result
      .then((confirmed: boolean) => !!confirmed)
      .catch(() => false);
  }

  private moveConfirmationMessage(
    invoice: InvoiceBoardResponseDto,
    fromLabel: string,
    targetStatusName: string,
  ): string {
    let message = `Move the invoice for ${this.getCustomerDisplayName(invoice)} ` +
      `(#${invoice.InvoiceNumber}) from ${fromLabel} to ${this.getSectionLabel(targetStatusName)}?`;
    if (targetStatusName === 'CUSTOMER_REVIEW' || targetStatusName === 'AWAITING_PAYMENT') {
      message += ' The customer will be notified by email.';
    }
    return message;
  }

  private moveInvoice(invoice: InvoiceBoardResponseDto, targetStatusName: string, alreadyMoved = false): void {
    const targetStatus = this.invoiceStatuses.find(s => s.Status === targetStatusName);
    if (!targetStatus) {
      this.alertService.showSnackBar('Unable to move invoice: statuses are not loaded.');
      return;
    }

    this.subs.push(this.invoiceService.updateInvoice(invoice.InvoiceId, {
      InvoiceStatusId: targetStatus.InvoiceStatusId
    }).subscribe({
      next: () => {
        invoice.InvoiceStatusId = targetStatus.InvoiceStatusId;
        invoice.InvoiceStatus = targetStatusName;
        if (!alreadyMoved) {
          this.regroup();
        }
        this.alertService.showSnackBar(`Invoice moved to ${this.getSectionLabel(targetStatusName)}.`);
      },
      error: (error) => {
        console.error('[Invoices] Failed to move invoice:', error);
        this.alertService.showSnackBar('Failed to move invoice.');
        this.loadBoard();
      }
    }));
  }

  private getSectionLabel(statusName: string): string {
    return this.sections.find(s => s.status === statusName)?.label || statusName;
  }

  // ============= CUSTOMER REVIEW: REJECTIONS =============

  isRejected(workItem: InvoiceBoardWorkItemDto): boolean {
    return workItem.WorkItemId in this.pendingRejections || !!workItem.RejectionReason;
  }

  /** Staged locally this session (still undoable), as opposed to persisted. */
  isPendingRejection(workItem: InvoiceBoardWorkItemDto): boolean {
    return workItem.WorkItemId in this.pendingRejections;
  }

  rejectionReasonFor(workItem: InvoiceBoardWorkItemDto): string {
    return this.pendingRejections[workItem.WorkItemId] ?? workItem.RejectionReason ?? '';
  }

  pendingRejectionCount(invoice: InvoiceBoardResponseDto): number {
    return (this.allWorkItems[invoice.InvoiceId] || [])
      .filter(workItem => workItem.WorkItemId in this.pendingRejections).length;
  }

  rejectWorkItem(workItem: InvoiceBoardWorkItemDto): void {
    const modalRef = this.ngbModal.open(RejectWorkItemModalComponent, { centered: true });
    modalRef.componentInstance.modalRef = modalRef;
    modalRef.componentInstance.workItemTitle = workItem.Title;

    modalRef.result.then((result: { reason: string } | undefined) => {
      if (result) {
        this.pendingRejections[workItem.WorkItemId] = result.reason;
      }
    }).catch(() => { /* dismissed */ });
  }

  undoRejection(workItem: InvoiceBoardWorkItemDto): void {
    delete this.pendingRejections[workItem.WorkItemId];
  }

  /**
   * Submit the customer's review. Full approval moves the invoice to TODO;
   * any rejected work items are persisted with their reason and the invoice
   * returns to MECHANIC_REVIEW so the shop can remove or revise them.
   */
  submitCustomerReview(invoice: InvoiceBoardResponseDto): void {
    const rejected = (this.allWorkItems[invoice.InvoiceId] || [])
      .filter(workItem => workItem.WorkItemId in this.pendingRejections);

    // Staff record the decision on the customer's behalf, so the copy is
    // written from their side rather than the customer's.
    const onBehalf = !this.canApprove(invoice);

    let message: string;
    if (rejected.length === 0) {
      message = onBehalf
        ? 'Approve all charges on the customer\'s behalf? The invoice will move to the shop\'s to-do list.'
        : 'Approve all charges? The invoice will move to the shop\'s to-do list.';
    } else {
      const items = `${rejected.length} rejected item${rejected.length === 1 ? '' : 's'}`;
      message = onBehalf
        ? `Submit the customer's review with ${items}? The invoice will go back to the shop for another review.`
        : `Submit your review with ${items}? The invoice will go back to the shop for another review.`;
    }

    this.confirmMove(message, rejected.length === 0 ? 'Approve' : 'Submit', 'Submit Review?').then((confirmed) => {
      if (!confirmed) return;
      this.persistCustomerReview(invoice, rejected);
    });
  }

  private persistCustomerReview(invoice: InvoiceBoardResponseDto, rejected: WorkItemVm[]): void {
    if (rejected.length === 0) {
      this.moveInvoice(invoice, 'TODO');
      return;
    }

    this.subs.push(forkJoin(
      rejected.map(workItem => this.invoiceService.updateWorkItem(workItem.WorkItemId, {
        RejectionReason: this.pendingRejections[workItem.WorkItemId] || 'No reason provided'
      }))
    ).subscribe({
      next: () => {
        for (const workItem of rejected) {
          delete this.pendingRejections[workItem.WorkItemId];
        }
        this.alertService.showSnackBar('Review submitted. The rejected items are going back to the shop.');
        this.moveInvoice(invoice, 'MECHANIC_REVIEW');
      },
      error: (error) => {
        console.error('[Invoices] Failed to submit rejections:', error);
        this.alertService.showSnackBar('Failed to submit review.');
        this.loadBoard();
      }
    }));
  }

  // ============= WORK ITEM MOVES (IN_PROGRESS COLUMNS) =============

  onWorkItemDropped(event: CdkDragDrop<WorkItemVm[]>): void {
    if (event.previousContainer === event.container) return;

    transferArrayItem(
      event.previousContainer.data,
      event.container.data,
      event.previousIndex,
      event.currentIndex
    );

    const workItem = event.item.data as WorkItemVm;

    // Check ownership - if not allowed, snap back with a toast
    if (!this.canMoveWorkItem(workItem)) {
      this.alertService.showSnackBar('You cannot move a work item that is not assigned to you.');
      this.regroup();
      return;
    }

    const targetStatusName = event.container.id.split('::')[2];
    this.persistWorkItemStatus(workItem, targetStatusName, false);
  }

  /** Mobile fallback: move a work item between lanes with a button. */
  moveWorkItemTo(workItem: WorkItemVm, targetStatusName: string): void {
    this.persistWorkItemStatus(workItem, targetStatusName, true);
  }

  private persistWorkItemStatus(workItem: WorkItemVm, targetStatusName: string, regroupAfter: boolean): void {
    const targetStatus = this.workItemStatuses.find(s => s.Status === targetStatusName);
    if (!targetStatus) {
      this.alertService.showSnackBar('Unable to move work item: statuses are not loaded.');
      this.loadBoard();
      return;
    }

    // Moving an unassigned work item claims it for the current mechanic.
    const claiming = !workItem.EmpUserId && !!this.user?.userId;

    this.subs.push(this.invoiceService.updateWorkItem(workItem.WorkItemId, {
      WorkItemStatusId: targetStatus.WorkItemStatusId,
      ...(claiming ? { EmpUserId: this.user!.userId } : {})
    }).subscribe({
      next: () => {
        workItem.WorkItemStatusId = targetStatus.WorkItemStatusId;
        workItem.WorkItemStatus = targetStatusName;
        if (claiming) {
          workItem.EmpUserId = this.user!.userId;
          workItem.EmpFirstName = this.user!.firstName;
          workItem.EmpLastName = this.user!.lastName;
        }
        // Keep the source board entry in sync so outstanding-work checks are right.
        this.syncWorkItemToBoard(workItem);
        // Drag & drop already moved the chip visually; button moves need a regroup.
        if (regroupAfter) {
          this.regroup();
        }
      },
      error: (error) => {
        console.error('[Invoices] Failed to move work item:', error);
        this.alertService.showSnackBar('Failed to move work item.');
        this.loadBoard();
      }
    }));
  }

  private syncWorkItemToBoard(updated: WorkItemVm): void {
    for (const invoice of this.board) {
      for (const vehicle of invoice.Vehicles) {
        const workItem = vehicle.WorkItems.find(wi => wi.WorkItemId === updated.WorkItemId);
        if (workItem) {
          workItem.WorkItemStatusId = updated.WorkItemStatusId;
          workItem.WorkItemStatus = updated.WorkItemStatus;
          workItem.EmpUserId = updated.EmpUserId;
          workItem.EmpFirstName = updated.EmpFirstName;
          workItem.EmpLastName = updated.EmpLastName;
          return;
        }
      }
    }
  }

  // ============= INVOICE PDF =============

  openInvoicePdf(invoiceId: string): void {
    this.subs.push(this.invoiceService.getInvoicePdfUrl(invoiceId).subscribe({
      next: (res) => window.open(res.url, '_blank'),
      error: (error) => {
        console.error('[Invoices] Failed to open invoice PDF:', error);
        this.alertService.showSnackBar('Failed to open invoice PDF.');
      }
    }));
  }

  // ============= INVOICE DELETE =============

  deleteInvoice(invoice: InvoiceBoardResponseDto): void {
    const confirmRef = this.ngbModal.open(ConfirmationModalComponent, { centered: true });
    confirmRef.componentInstance.modalRef = confirmRef;
    confirmRef.componentInstance.title = 'Delete Invoice?';
    confirmRef.componentInstance.message =
      `Delete the invoice for ${this.getCustomerDisplayName(invoice)} (#${invoice.InvoiceNumber}) ` +
      'and all of its work items? This cannot be undone.';
    confirmRef.componentInstance.confirmButtonText = 'Delete';
    confirmRef.componentInstance.cancelButtonText = 'Cancel';

    confirmRef.result.then((confirmed: boolean) => {
      if (!confirmed) return;

      this.subs.push(this.invoiceService.deleteInvoice(invoice.InvoiceId).subscribe({
        next: () => {
          this.alertService.showSnackBar('Invoice deleted.');
          this.loadBoard();
        },
        error: (error) => {
          console.error('[Invoices] Failed to delete invoice:', error);
          this.alertService.showSnackBar('Failed to delete invoice.');
        }
      }));
    }).catch(() => { /* dismissed */ });
  }

  // ============= WORK ITEM ADD / EDIT / REMOVE =============

  /** Mechanic removes a work item from the invoice (e.g. after a customer rejection). */
  removeWorkItem(workItem: InvoiceBoardWorkItemDto): void {
    const confirmRef = this.ngbModal.open(ConfirmationModalComponent, { centered: true });
    confirmRef.componentInstance.modalRef = confirmRef;
    confirmRef.componentInstance.title = 'Remove Work Item?';
    confirmRef.componentInstance.message =
      `Remove "${workItem.Title}" from this invoice? This cannot be undone.`;
    confirmRef.componentInstance.confirmButtonText = 'Remove';
    confirmRef.componentInstance.cancelButtonText = 'Cancel';

    confirmRef.result.then((confirmed: boolean) => {
      if (!confirmed) return;

      this.subs.push(this.invoiceService.deleteWorkItem(workItem.WorkItemId).subscribe({
        next: () => {
          this.alertService.showSnackBar('Work item removed.');
          this.loadBoard();
        },
        error: (error) => {
          console.error('[Invoices] Failed to remove work item:', error);
          this.alertService.showSnackBar('Failed to remove work item.');
        }
      }));
    }).catch(() => { /* dismissed */ });
  }

  openWorkItemModal(invoice: InvoiceBoardResponseDto, workItem: InvoiceBoardWorkItemDto | null = null, readOnly = false): void {
    const modalRef = this.ngbModal.open(WorkItemModalComponent, {
      size: 'lg',
      centered: true,
      backdrop: 'static'
    });

    modalRef.componentInstance.modalRef = modalRef;
    modalRef.componentInstance.vehicles = invoice.Vehicles;
    modalRef.componentInstance.mechanics = this.mechanics;
    modalRef.componentInstance.workItem = workItem;
    modalRef.componentInstance.readOnly = readOnly;
    // Deleting changes the charges, so it is only offered during MECHANIC_REVIEW.
    modalRef.componentInstance.allowDelete = invoice.InvoiceStatus === 'MECHANIC_REVIEW';
    // Images are staff-editable through every invoice stage, even view-only ones.
    modalRef.componentInstance.canEditImages = this.hasEditAll;

    modalRef.result.then((result: WorkItemModalResult | undefined) => {
      if (!result || readOnly) return;
      if (result.mode === 'delete' && workItem) {
        this.removeWorkItem(workItem);
        return;
      }
      this.handleWorkItemModalResult(invoice, result);
    }).catch(() => { /* dismissed */ });
  }

  private handleWorkItemModalResult(invoice: InvoiceBoardResponseDto, result: WorkItemModalResult): void {
    // Past MECHANIC_REVIEW, adding a work item or changing monetary values
    // sends the invoice back for another mechanic review + customer approval.
    const needsRegression = invoice.InvoiceStatus !== 'MECHANIC_REVIEW' &&
      (result.mode === 'create' || result.monetaryChanged);

    if (!needsRegression) {
      this.saveWorkItem(invoice, result, false);
      return;
    }

    const confirmRef = this.ngbModal.open(ConfirmationModalComponent, { centered: true });
    confirmRef.componentInstance.modalRef = confirmRef;
    confirmRef.componentInstance.title = 'Return to Mechanic Review?';
    confirmRef.componentInstance.message =
      'This change affects the invoice\'s charges, so the invoice will move back to Mechanic Review ' +
      'and the customer will need to approve the updated charges again. Do you want to continue? ' +
      'Choose "Save, Keep Status" to save the change without sending the invoice back.';
    confirmRef.componentInstance.confirmButtonText = 'Yes, Continue';
    confirmRef.componentInstance.tertiaryButtonText = 'Save, Keep Status';
    confirmRef.componentInstance.cancelButtonText = 'Cancel';
    confirmRef.componentInstance.confirmButtonClass = 'btn-warning';

    // 'tertiary' is the override: save the change but leave the status alone.
    confirmRef.result.then((choice: boolean | 'tertiary') => {
      if (choice === true) {
        this.saveWorkItem(invoice, result, true);
      } else if (choice === 'tertiary') {
        this.saveWorkItem(invoice, result, false);
      }
    }).catch(() => { /* dismissed */ });
  }

  private saveWorkItem(invoice: InvoiceBoardResponseDto, result: WorkItemModalResult, regressInvoice: boolean): void {
    const todoWorkItemStatus = this.workItemStatuses.find(s => s.Status === 'TODO');
    const mechanicReviewStatus = this.invoiceStatuses.find(s => s.Status === 'MECHANIC_REVIEW');

    if (result.mode === 'create' && !todoWorkItemStatus) {
      this.alertService.showSnackBar('Unable to save work item: statuses are not loaded.');
      return;
    }
    if (regressInvoice && !mechanicReviewStatus) {
      this.alertService.showSnackBar('Unable to save work item: statuses are not loaded.');
      return;
    }

    // Only create/edit results reach this point, so the form data is present.
    const data = result.data!;
    const request = result.mode === 'create'
      ? this.invoiceService.createWorkItem({
          InvoiceVehicleId: result.invoiceVehicleId,
          WorkItemStatusId: todoWorkItemStatus!.WorkItemStatusId,
          EmpUserId: data.EmpUserId ?? undefined,
          Title: data.Title,
          Miles: data.Miles,
          ShopHours: data.ShopHours,
          PartsPricing: data.PartsPricing,
          FlatFee: data.FlatFee ?? undefined,
          FlatFeeTaxable: data.FlatFeeTaxable,
          Notes: data.Notes
        })
      // The rejection is only cleared when the mechanic explicitly opted in
      // via the modal's checkbox.
      : this.invoiceService.updateWorkItem(result.workItemId!, {
          ...data,
          ...(result.clearRejection ? { RejectionReason: null } : {})
        });

    this.subs.push(request.pipe(
      // Upload any staged images now that the work item id is known. A failed
      // upload warns but doesn't fail the save (same as the vehicle modal).
      switchMap((savedWorkItem) => {
        const uploads = (result.pendingImages ?? []).map(pending =>
          this.invoiceService.uploadWorkItemImage(savedWorkItem.WorkItemId, pending.file, pending.imageTypeId).pipe(
            catchError(error => {
              console.error('[Invoices] Failed to upload work item image:', error);
              this.alertService.showSnackBar('Work item saved, but an image upload failed.');
              return of(null);
            })
          )
        );
        return uploads.length > 0 ? forkJoin(uploads) : of([]);
      }),
      switchMap(() => regressInvoice
        ? this.invoiceService.updateInvoice(invoice.InvoiceId, {
            InvoiceStatusId: mechanicReviewStatus!.InvoiceStatusId
          })
        : of(null))
    ).subscribe({
      next: () => {
        this.alertService.showSnackBar(regressInvoice
          ? 'Work item saved. Invoice returned to Mechanic Review.'
          : 'Work item saved.');
        this.loadBoard();
      },
      error: (error) => {
        console.error('[Invoices] Failed to save work item:', error);
        this.alertService.showSnackBar('Failed to save work item.');
        this.loadBoard();
      }
    }));
  }
}
